import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { notifyNewRendezVous } from '../../lib/notifications';
import { useOfflineMutation } from '../../hooks/useOfflineMutation';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const specialties = [
  { name: 'Médecine Générale', icon: 'ri-stethoscope-line' },
  { name: 'Urologie et Chirurgie Générale', icon: 'ri-surgical-mask-line' },
  { name: 'Maternité (Sage-femme)', icon: 'ri-parent-line' },
  { name: 'Gynécologie', icon: 'ri-women-line' },
  { name: 'Psychologie et Psychiatrie', icon: 'ri-mental-health-line' },
  { name: 'Pédiatrie', icon: 'ri-bear-smile-line' },
  { name: 'ORL', icon: 'ri-ear-line' },
  { name: 'Ophtalmologie', icon: 'ri-eye-line' },
  { name: 'Kinésithérapie', icon: 'ri-walk-line' },
  { name: 'Traumatologie et Orthopédie', icon: 'ri-bone-line' },
  { name: 'Hématologie', icon: 'ri-drop-line' },
  { name: 'Dermatologie', icon: 'ri-hand-sanitizer-line' },
  { name: 'Hépato Gastro Entérologie', icon: 'ri-capsule-line' },
  { name: 'Cardiologie', icon: 'ri-heart-pulse-line' },
  { name: 'Échographie Radiologie', icon: 'ri-scan-line' },
  { name: 'Médecine physique et Réadaptation', icon: 'ri-wheelchair-line' },
  { name: 'Pneumologie', icon: 'ri-lungs-line' },
  { name: 'Endocrinologie', icon: 'ri-test-tube-line' },
  { name: 'Neurologie et neurochirurgie', icon: 'ri-brain-line' },
  { name: 'Vaccination', icon: 'ri-syringe-line' },
  { name: 'Laboratoire d\'analyse bio médicales', icon: 'ri-microscope-line' },
];

// Créneaux par défaut si le médecin n'a pas défini de disponibilités
const defaultTimeSlots = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
];

interface Medecin {
  id: string;
  nom: string;
  prenom: string;
  specialite: string;
  type: 'medecin' | 'sage_femme';
}

export default function RendezVousPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedMedecin, setSelectedMedecin] = useState<Medecin | null>(null);
  const [availableMedecins, setAvailableMedecins] = useState<Medecin[]>([]);
  const [bookedSlots, setBookedSlots] = useState<string[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [patientInfo, setPatientInfo] = useState<any>(null);
  const { mutate } = useOfflineMutation<any>();

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (role !== 'patient') {
      navigate('/', { replace: true });
      return;
    }
    loadPatientInfo();
  }, [user, role]);

  useEffect(() => {
    if (selectedSpecialty) {
      loadAvailableMedecins();
    }
  }, [selectedSpecialty]);

  useEffect(() => {
    if (selectedMedecin && selectedDate) {
      loadAvailableTimeSlots();
      loadBookedSlots();
    }
  }, [selectedMedecin, selectedDate]);

  const loadPatientInfo = async () => {
    if (!user) return;
    
    const { data } = await supabase
      .from('patients')
      .select('*')
      .eq('user_id', user.id)
      .single();
    
    if (data) {
      setPatientInfo(data);
    }
  };

  const loadAvailableMedecins = async () => {
    if (selectedSpecialty === 'Maternité (Sage-femme)') {
      // Charger les sages-femmes disponibles
      const { data } = await supabase
        .from('sage_femmes')
        .select('id, nom, prenom')
        .eq('bloque', false);

      if (data && data.length > 0) {
        const practitioners = data.map(sf => ({
          id: sf.id,
          nom: sf.nom,
          prenom: sf.prenom,
          specialite: 'Maternité (Sage-femme)',
          type: 'sage_femme' as const,
        }));
        setAvailableMedecins(practitioners);
        setSelectedMedecin(practitioners[0]);
      } else {
        setAvailableMedecins([]);
        setSelectedMedecin(null);
      }
      return;
    }

    // Pour les autres spécialités (dont Gynécologie), charger les médecins
    const { data } = await supabase
      .from('medecins')
      .select('id, nom, prenom, specialite')
      .ilike('specialite', `%${selectedSpecialty}%`)
      .eq('bloque', false);

    if (data && data.length > 0) {
      const practitioners = data.map(m => ({
        ...m,
        type: 'medecin' as const,
      }));
      setAvailableMedecins(practitioners);
      setSelectedMedecin(practitioners[0]);
    } else {
      setAvailableMedecins([]);
      setSelectedMedecin(null);
    }
  };

  const loadBookedSlots = async () => {
    if (!selectedMedecin || !selectedDate) return;

    try {
      const { data, error } = await supabase
        .from('rendez_vous')
        .select('heure_debut')
        .eq('medecin_id', selectedMedecin.id)
        .eq('date_rdv', selectedDate)
        .in('statut', ['en_attente', 'en_cours']);

      if (error) {
        console.error('Erreur lors du chargement des créneaux:', error);
        return;
      }

      if (data) {
        const bookedTimes = data.map(slot => slot.heure_debut);
        console.log('Créneaux réservés pour le', selectedDate, ':', bookedTimes);
        setBookedSlots(bookedTimes);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des créneaux:', error);
    }
  };

  const loadAvailableTimeSlots = async () => {
    if (!selectedMedecin || !selectedDate) return;

    try {
      // Récupérer le jour de la semaine (0 = dimanche, 1 = lundi, ..., 6 = samedi)
      const date = new Date(selectedDate + 'T00:00:00');
      const dayOfWeek = date.getDay();
      
      // Convertir en format français (1 = lundi, 7 = dimanche)
      const jourSemaine = dayOfWeek === 0 ? 7 : dayOfWeek;

      // Charger les disponibilités du médecin pour ce jour
      const { data: disponibilites, error } = await supabase
        .from('disponibilites_medecins')
        .select('heure_debut, heure_fin')
        .eq('medecin_id', selectedMedecin.id)
        .eq('jour_semaine', jourSemaine)
        .eq('actif', true);

      if (error) {
        console.error('Erreur lors du chargement des disponibilités:', error);
        setAvailableTimeSlots(defaultTimeSlots);
        return;
      }

      // Si le médecin n'a pas de disponibilités définies pour ce jour
      if (!disponibilites || disponibilites.length === 0) {
        console.log('Aucune disponibilité définie, utilisation des créneaux par défaut');
        setAvailableTimeSlots(defaultTimeSlots);
        return;
      }

      // Générer les créneaux de 30 minutes basés sur les disponibilités
      const slots: string[] = [];
      
      disponibilites.forEach(dispo => {
        const [startHour, startMin] = dispo.heure_debut.split(':').map(Number);
        const [endHour, endMin] = dispo.heure_fin.split(':').map(Number);
        
        let currentTime = new Date();
        currentTime.setHours(startHour, startMin, 0, 0);
        
        const endTime = new Date();
        endTime.setHours(endHour, endMin, 0, 0);
        
        // Générer des créneaux de 30 minutes
        while (currentTime < endTime) {
          const timeStr = currentTime.toTimeString().slice(0, 5);
          if (!slots.includes(timeStr)) {
            slots.push(timeStr);
          }
          currentTime.setMinutes(currentTime.getMinutes() + 30);
        }
      });

      // Trier les créneaux par ordre chronologique
      slots.sort();
      
      // Filtrer les créneaux déjà passés si la date est aujourd'hui
      const today = new Date().toISOString().split('T')[0];
      const now = new Date();
      const filteredSlots = slots.filter(time => {
        if (selectedDate === today) {
          const [h, m] = time.split(':').map(Number);
          const slotTime = new Date();
          slotTime.setHours(h, m, 0, 0);
          return slotTime > now;
        }
        return true;
      });
      
      console.log('Créneaux disponibles pour le médecin:', filteredSlots);
      setAvailableTimeSlots(filteredSlots);

    } catch (error) {
      console.error('Erreur lors du chargement des disponibilités:', error);
      setAvailableTimeSlots(defaultTimeSlots);
    }
  };

  const handleTimeSlotClick = (time: string, isBooked: boolean) => {
    if (isBooked) {
      alert('Ce créneau horaire est déjà réservé. Veuillez en choisir un autre.');
      return;
    }
    setSelectedTime(time);
    
    // Rafraîchir les créneaux pour s'assurer qu'ils sont à jour
    loadBookedSlots();
  };

  const handleBooking = async () => {
    if (!selectedSpecialty || !selectedDate || !selectedTime || !patientInfo || !selectedMedecin) {
      return;
    }

    // Vérifier que le créneau n'est pas dans le passé
    const now = new Date();
    const appointmentDateTime = new Date(selectedDate + 'T' + selectedTime);
    if (appointmentDateTime <= now) {
      alert('Impossible de réserver un créneau déjà passé. Veuillez choisir une heure future.');
      setSelectedTime('');
      return;
    }

    // Vérifier si le créneau est déjà réservé avant de continuer
    if (bookedSlots.includes(selectedTime)) {
      alert('Ce créneau horaire est déjà réservé. Veuillez en choisir un autre.');
      setSelectedTime('');
      return;
    }

    setLoading(true);
    const online = navigator.onLine;

    try {
      // Double vérification (seulement en ligne)
      if (online) {
      const { data: existingBookings, error: checkError } = await supabase
        .from('rendez_vous')
        .select('id')
        .eq('medecin_id', selectedMedecin.id)
        .eq('date_rdv', selectedDate)
        .eq('heure_debut', selectedTime)
        .in('statut', ['en_attente', 'en_cours']);

      if (checkError) {
        console.error('Erreur lors de la vérification:', checkError);
        alert('Erreur lors de la vérification du créneau. Veuillez réessayer.');
        setLoading(false);
        return;
      }

      if (existingBookings && existingBookings.length > 0) {
        alert('Ce créneau horaire est déjà réservé par un autre patient. Veuillez en choisir un autre.');
        setLoading(false);
        await loadBookedSlots();
        setSelectedTime('');
        return;
      }

      // ✅ NOUVELLE VÉRIFICATION : Empêcher le patient d'avoir 2 RDV à la même heure
      const { data: patientConflict, error: patientCheckError } = await supabase
        .from('rendez_vous')
        .select('id, specialite')
        .eq('patient_id', patientInfo.id)
        .eq('date_rdv', selectedDate)
        .eq('heure_debut', selectedTime)
        .in('statut', ['en_attente', 'en_cours']);

      if (patientCheckError) {
        console.error('Erreur lors de la vérification patient:', patientCheckError);
        alert('Erreur lors de la vérification. Veuillez réessayer.');
        setLoading(false);
        return;
      }

      if (patientConflict && patientConflict.length > 0) {
        alert(`Vous avez déjà un rendez-vous à ${selectedTime} le ${new Date(selectedDate + 'T00:00:00').toLocaleDateString('fr-FR')}. Veuillez choisir une autre heure.`);
        setLoading(false);
        setSelectedTime('');
        return;
      }
      } // fin if (online)

      // Calculer l'heure de fin (30 minutes après le début)
      const [hours, minutes] = selectedTime.split(':');
      const startTime = new Date();
      startTime.setHours(parseInt(hours), parseInt(minutes), 0);
      const endTime = new Date(startTime.getTime() + 30 * 60000);
      const heureFinFormatted = endTime.toTimeString().slice(0, 5);

      const rdvData = {
        patient_id: patientInfo.id,
        medecin_id: selectedMedecin.id,
        specialite: selectedMedecin.specialite,
        date_rdv: selectedDate,
        heure_debut: selectedTime,
        heure_fin: heureFinFormatted,
        statut: 'en_attente',
      };

      await mutate({
        table: 'rendez_vous',
        operation: 'INSERT',
        data: rdvData,
        metadata: {
          summary: `RDV ${selectedMedecin.type === 'sage_femme' ? 'SF' : 'Dr'} ${selectedMedecin.nom} - ${selectedDate} ${selectedTime}`,
          performedBy: 'patient',
        },
        onlineExecutor: async () => {
          const { data: inserted, error } = await supabase
            .from('rendez_vous')
            .insert(rdvData)
            .select()
            .single();

          if (error) {
            if (error.code === '23505' || error.message.includes('unique') || error.message.includes('duplicate')) {
              throw new Error('Ce créneau horaire vient d\'être réservé par un autre patient.');
            }
            throw error;
          }
          return inserted;
        },
      });

      // Notifications (seulement en ligne)
      if (online) {
      try {
        const dateFormatted = new Date(selectedDate + 'T00:00:00').toLocaleDateString('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });

        // 1. Notification au PRATICIEN (médecin ou sage-femme)
        const { data: medecinData } = await supabase
          .from(selectedMedecin.type === 'sage_femme' ? 'sage_femmes' : 'medecins')
          .select('user_id, nom, prenom, email, telephone')
          .eq('id', selectedMedecin.id)
          .single();

        if (medecinData) {
          const medecinName = selectedMedecin.type === 'sage_femme' ? `Sage-femme ${medecinData.nom} ${medecinData.prenom}` : `Dr ${medecinData.nom} ${medecinData.prenom}`;
          const patientName = `${patientInfo.nom} ${patientInfo.prenom}`;

          // Notification système au médecin
          await notifyNewRendezVous(
            medecinData.user_id,
            patientInfo.nom,
            patientInfo.prenom,
            dateFormatted,
            selectedTime
          );

          // Email/SMS au médecin
          if (medecinData.email) {
            await supabase.functions.invoke('send-email', {
              body: {
                to: medecinData.email,
                toName: medecinName,
                subject: '📅 Nouveau rendez-vous confirmé',
                htmlContent: `
                  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                    <div style="background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
                      <h1 style="color: white; margin: 0;">📅 Nouveau rendez-vous</h1>
                    </div>
                    <div style="background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px;">
                      <h2 style="color: #1e40af;">Bonjour ${medecinName},</h2>
                      <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">
                        Un nouveau patient a pris rendez-vous avec vous :
                      </p>
                      <div style="background: white; border-left: 4px solid #3b82f6; padding: 20px; margin: 20px 0; border-radius: 5px;">
                        <p style="margin: 5px 0; color: #1f2937;"><strong>👤 Patient :</strong> ${patientName}</p>
                        <p style="margin: 5px 0; color: #1f2937;"><strong>📅 Date :</strong> ${dateFormatted}</p>
                        <p style="margin: 5px 0; color: #1f2937;"><strong>🕐 Heure :</strong> ${selectedTime}</p>
                        <p style="margin: 5px 0; color: #1f2937;"><strong>🩺 Spécialité :</strong> ${selectedMedecin.specialite}</p>
                        ${patientInfo.telephone ? `<p style="margin: 5px 0; color: #1f2937;"><strong>📞 Téléphone :</strong> ${patientInfo.telephone}</p>` : ''}
                      </div>
                      <div style="text-align: center; margin: 30px 0;">
                        <a href="${window.location.origin}/medecin/rendez-vous" 
                           style="background: #3b82f6; color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; font-weight: bold; display: inline-block;">
                          Voir mes rendez-vous
                        </a>
                      </div>
                    </div>
                  </div>
                `
              }
            });
            console.log('✅ Email envoyé au médecin');
          } else if (medecinData.telephone) {
            await supabase.functions.invoke('send-sms', {
              body: {
                to: medecinData.telephone,
                message: `Nouveau RDV confirmé : ${patientName} le ${dateFormatted} à ${selectedTime}. Spécialité : ${selectedMedecin.specialite}.`
              }
            });
            console.log('✅ SMS envoyé au médecin');
          }
        }

        // 2. Notification au PATIENT
        const patientName = `${patientInfo.prenom} ${patientInfo.nom}`;
        const medecinFullName = selectedMedecin.type === 'sage_femme' ? `Sage-femme ${selectedMedecin.nom} ${selectedMedecin.prenom}` : `Dr ${selectedMedecin.nom} ${selectedMedecin.prenom}`;

        if (patientInfo.email) {
          await supabase.functions.invoke('send-email', {
            body: {
              to: patientInfo.email,
              toName: patientName,
              subject: '✅ Rendez-vous confirmé - Clinique Sainte-Famille',
              htmlContent: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <div style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
                    <h1 style="color: white; margin: 0;">✅ Rendez-vous confirmé</h1>
                  </div>
                  <div style="background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px;">
                    <h2 style="color: #059669;">Bonjour ${patientName},</h2>
                    <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">
                      Votre rendez-vous a été confirmé avec succès !
                    </p>
                    <div style="background: white; border-left: 4px solid #10b981; padding: 20px; margin: 20px 0; border-radius: 5px;">
                      <p style="margin: 5px 0; color: #1f2937;"><strong>👨‍⚕️ Médecin :</strong> ${medecinFullName}</p>
                      <p style="margin: 5px 0; color: #1f2937;"><strong>🩺 Spécialité :</strong> ${selectedMedecin.specialite}</p>
                      <p style="margin: 5px 0; color: #1f2937;"><strong>📅 Date :</strong> ${dateFormatted}</p>
                      <p style="margin: 5px 0; color: #1f2937;"><strong>🕐 Heure :</strong> ${selectedTime}</p>
                    </div>
                    <div style="background: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 20px 0; border-radius: 5px;">
                      <p style="color: #92400e; margin: 0; font-size: 14px;">
                        ⏰ Vous recevrez un rappel 24h avant votre rendez-vous.
                      </p>
                    </div>
                    <div style="text-align: center; margin: 30px 0;">
                      <a href="${window.location.origin}/patient/rendez-vous" 
                         style="background: #10b981; color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; font-weight: bold; display: inline-block;">
                        Voir mes rendez-vous
                      </a>
                    </div>
                    <p style="color: #6b7280; font-size: 14px; margin-top: 30px;">
                      📍 Clinique Sainte-Famille<br>
                      📞 Pour toute question, contactez-nous
                    </p>
                  </div>
                </div>
              `
            }
          });
          console.log('✅ Email de confirmation envoyé au patient');
        } else if (patientInfo.telephone) {
          await supabase.functions.invoke('send-sms', {
            body: {
              to: patientInfo.telephone,
              message: `Clinique Sainte-Famille : RDV confirmé avec ${medecinFullName} (${selectedMedecin.specialite}) le ${dateFormatted} à ${selectedTime}. Vous recevrez un rappel 24h avant.`
            }
          });
          console.log('✅ SMS de confirmation envoyé au patient');
        }

      } catch (notifError) {
        console.error('⚠️ Erreur envoi notifications:', notifError);
      }
      } // fin if (online)

      setSuccess(true);
      setSelectedSpecialty('');
      setSelectedDate('');
      setSelectedTime('');
      setSelectedMedecin(null);
      setAvailableMedecins([]);
      
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de la réservation:', error);
      alert('Une erreur est survenue lors de la réservation. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    return maxDate.toISOString().split('T')[0];
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <div className="flex-1 py-6 sm:py-10 md:py-12 px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-4xl font-bold text-clinic-blue-dark mb-2">Prendre un rendez-vous</h1>
            <p className="text-sm sm:text-base text-gray-600">Choisissez une spécialité, une date et un horaire disponible</p>
          </div>

          {success && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-clinic-green/10 border-2 border-clinic-green/30 text-clinic-green rounded-2xl text-sm sm:text-base">
              <i className="ri-checkbox-circle-line mr-2"></i>
              Votre rendez-vous a été confirmé avec succès !
            </div>
          )}

          {/* Sélection de la spécialité */}
          {!selectedSpecialty && (
            <div className="bg-white p-4 sm:p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
              <h2 className="text-xl sm:text-2xl font-bold text-clinic-blue-dark mb-4 sm:mb-6">Choisissez une spécialité</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {specialties.map((specialty, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedSpecialty(specialty.name)}
                    className="bg-white p-4 sm:p-6 rounded-2xl shadow-md hover:shadow-xl transition-all border-2 border-clinic-blue/10 hover:border-clinic-green hover:scale-105 cursor-pointer group text-left"
                  >
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-clinic-blue/10 rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-clinic-green/10 transition-colors">
                      <i className={`${specialty.icon} text-xl sm:text-2xl text-clinic-blue group-hover:text-clinic-green transition-colors`}></i>
                    </div>
                    <h3 className="font-semibold text-sm sm:text-base text-clinic-blue-dark group-hover:text-clinic-green transition-colors">
                      {specialty.name}
                    </h3>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sélection de la date et de l'heure */}
          {selectedSpecialty && (
            <div className="bg-white p-4 sm:p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-clinic-blue-dark">
                    {selectedSpecialty}
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600 mt-1">Sélectionnez une date et un horaire</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedSpecialty('');
                    setSelectedDate('');
                    setSelectedTime('');
                    setSelectedMedecin(null);
                    setAvailableMedecins([]);
                    setAvailableTimeSlots([]);
                  }}
                  className="px-4 py-2 text-clinic-blue hover:text-clinic-blue-dark transition-colors whitespace-nowrap text-sm sm:text-base self-start sm:self-auto"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Changer de spécialité
                </button>
              </div>

              {availableMedecins.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-user-line text-3xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-600">Aucun praticien disponible pour cette spécialité</p>
                </div>
              ) : (
                <>
                  {/* Sélection du médecin */}
                  {availableMedecins.length > 1 && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-clinic-blue-dark mb-3">
                        Choisir un praticien
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {availableMedecins.map((medecin) => (
                          <button
                            key={medecin.id}
                            onClick={() => {
                              setSelectedMedecin(medecin);
                              setSelectedTime('');
                              setAvailableTimeSlots([]);
                            }}
                            className={`p-4 rounded-2xl border-2 transition-all text-left ${
                              selectedMedecin?.id === medecin.id
                                ? 'border-clinic-green bg-clinic-green/5'
                                : 'border-clinic-blue/10 hover:border-clinic-blue/30'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-clinic-blue/10 rounded-xl flex items-center justify-center">
                                <i className="ri-user-line text-xl text-clinic-blue"></i>
                              </div>
                              <div>
                                <p className="font-semibold text-clinic-blue-dark">
                                  {medecin.type === 'sage_femme' ? 'Sage-femme' : 'Dr'} {medecin.nom} {medecin.prenom}
                                </p>
                                <p className="text-sm text-gray-600">{medecin.specialite}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedMedecin && (
                    <>
                      {availableMedecins.length === 1 && (
                        <div className="mb-6 p-4 bg-clinic-blue/5 rounded-2xl">
                          <p className="text-clinic-blue-dark">
                            <strong>{selectedMedecin.type === 'sage_femme' ? 'Sage-femme' : 'Médecin'} :</strong> {selectedMedecin.type === 'sage_femme' ? '' : 'Dr '}{selectedMedecin.nom} {selectedMedecin.prenom}
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Sélection de la date */}
                        <div>
                          <label className="block text-sm font-medium text-clinic-blue-dark mb-3">
                            Date du rendez-vous
                          </label>
                          <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => {
                              setSelectedDate(e.target.value);
                              setSelectedTime('');
                              setAvailableTimeSlots([]);
                            }}
                            min={getMinDate()}
                            max={getMaxDate()}
                            className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                          />
                        </div>

                        {/* Sélection de l'heure */}
                        {selectedDate && (
                          <div>
                            <label className="block text-sm font-medium text-clinic-blue-dark mb-3">
                              Horaire disponible
                            </label>
                            {availableTimeSlots.length === 0 ? (
                              <div className="text-center py-8">
                                <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                                  <i className="ri-calendar-close-line text-2xl text-gray-400"></i>
                                </div>
                                <p className="text-gray-600 text-sm">Aucune disponibilité ce jour</p>
                              </div>
                            ) : (
                              <>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                                  {availableTimeSlots.map((time) => {
                                    const isBooked = bookedSlots.includes(time);
                                    return (
                                      <button
                                        key={time}
                                        onClick={() => handleTimeSlotClick(time, isBooked)}
                                        className={`px-2 sm:px-3 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all ${
                                          isBooked
                                            ? 'bg-blue-500 text-white cursor-pointer'
                                            : selectedTime === time
                                            ? 'bg-clinic-green text-white shadow-lg'
                                            : 'bg-clinic-blue/10 text-clinic-blue hover:bg-clinic-blue hover:text-white'
                                        }`}
                                      >
                                        {time}
                                      </button>
                                    );
                                  })}
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                  <i className="ri-information-line mr-1"></i>
                                  Les créneaux en bleu sont déjà réservés
                                </p>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {selectedDate && selectedTime && (
                        <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-clinic-blue/5 rounded-2xl">
                          <h3 className="text-base sm:text-lg font-semibold text-clinic-blue-dark mb-4">Récapitulatif</h3>
                          <div className="space-y-2 text-sm text-gray-700">
                            <p><strong>{selectedMedecin.type === 'sage_femme' ? 'Sage-femme' : 'Médecin'} :</strong> {selectedMedecin.type === 'sage_femme' ? '' : 'Dr '}{selectedMedecin.nom} {selectedMedecin.prenom}</p>
                            <p><strong>Spécialité :</strong> {selectedMedecin.specialite}</p>
                            <p><strong>Date :</strong> {new Date(selectedDate + 'T00:00:00').toLocaleDateString('fr-FR', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}</p>
                            <p><strong>Heure :</strong> {selectedTime}</p>
                          </div>
                          <button
                            onClick={handleBooking}
                            disabled={loading}
                            className="w-full mt-4 sm:mt-6 px-4 sm:px-6 py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm sm:text-base"
                          >
                            {loading ? 'Confirmation...' : 'Confirmer le rendez-vous'}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
